// =============================================================================
// AI DESIGN ENGINE
//
// This is a deterministic, validated design/edit interpreter for the check
// studio. It does not invent controls or values: every change it returns maps
// to a real CheckBuilder state field and an ID used by the studio. It starts
// from the caller's current design and returns a patch, so a narrow prompt
// such as "make the border gold" cannot reset unrelated choices.
// =============================================================================

import type { BorderStyle } from '../components/BorderlineEngine';
import { resolveBackVariant } from '../data/backVariants';
import { SEAL_COLOR_PRESETS } from '../utils/sealDesign';

export interface AiCheckFields {
    bankName: string;
    bankAddress: string;
    watermarkText: string;
    checkNumber: string;
    date: string;
    routingNumber: string;
    accountNumber: string;
    fractionalRouting: string;
    epcDigit: string;
    payeeName: string;
    payeeAddress: string;
    amount: number;
    memo: string;
    accountHolderName: string;
    accountHolderAddress: string;
    ein: string;
    lastFour: string;
    hoursWorked: string;
    payRate: string;
    payTotal: string;
    ytdGross: string;
    ytdNet: string;
    payeeFontSize: number;
    memoFontSize: number;
    micrFont: string;
    micrFontSize: number;
}

export interface AiBorderPatch {
    style?: BorderStyle;
    color?: string;
    secondaryColor?: string;
    thickness?: number;
    cornerRadius?: number;
}

export interface AiObjectMove {
    left?: number;
    top?: number;
    scaleX?: number;
    scaleY?: number;
    angle?: number;
}

export interface AiStudioState {
    checkStyle: string;
    selectedTexture: string;
    paperColor: string;
    selectedFont: string;
    borderConfig: {
        style: BorderStyle;
        color: string;
        secondaryColor?: string;
        thickness: number;
        cornerRadius: number;
    };
    textureOpacity: number;
    securityFeatures: Array<{ id: string; enabled: boolean }> | Record<string, boolean>;
    checkData: Partial<AiCheckFields> & Record<string, unknown>;
    handwritingStyle: string;
    signatureStyle: string;
    backSideStyle: string;
    sealColor: string;
    sealFinish: string;
    handwritingColor: string;
    handwritingRotation: number;
    showMicrBand: boolean;
    showAmountBorder: boolean;
    showMicrGuide: boolean;
    objectPositions: Record<string, AiObjectMove>;
    width: number;
    height: number;
}

export interface AiDesignResult {
    styles: Partial<{
        checkStyle: string;
        texture: string;
        paperColor: string;
        font: string;
        borderStyle: BorderStyle;
        borderColor: string;
        textureOpacity: number;
        primaryColor: string;
        secondaryColor: string;
        accentColor: string;
    }>;
    fields: Partial<AiCheckFields>;
    border: AiBorderPatch;
    controls: Partial<{
        handwritingStyle: string;
        signatureStyle: string;
        backSideStyle: string;
        sealColor: string;
        sealFinish: 'flat' | 'metallic';
        handwritingColor: string;
        handwritingRotation: number;
        showMicrBand: boolean;
        showAmountBorder: boolean;
        showMicrGuide: boolean;
        clearLogo: boolean;
        clearSignature: boolean;
    }>;
    layout: {
        moveMap: Record<string, AiObjectMove>;
        resetToDefault?: boolean;
        restoreDeleted?: boolean;
    };
    security: {
        /** Features to switch on. `features` is kept as a readable alias. */
        enable: string[];
        disable: string[];
        features: string[];
    };
    meta: {
        explanation: string;
        changes: string[];
        warnings: string[];
        bankDetected?: string;
        bankName?: string;
        confidence: number;
    };
}

export const AI_CHECK_STYLE_IDS = [
    'cashier', 'personal', 'business', 'payroll', 'payroll_classic',
    'payroll_karen', 'echeck', 'money_order', 'certified', 'counter',
    'bofa_classic', 'bofa_v2', 'us_treasury'
] as const;

export const AI_TEXTURE_IDS = [
    'linen', 'parchment', 'marble', 'security', 'plain', 'cotton',
    'particles', 'rough', 'guilloche_bg', 'blue_wave', 'prismatic',
    'laser_security', 'fluorescent_fibers', 'toner_adhesion',
    'nano_optical', 'interference', 'multi_tone', 'fiber_optic',
    'micro_embossed', 'geometric_diamond', 'circuit_board',
    'royal_damask', 'digital_hive', 'cloud_marble',
    // Design-library expansion (2026-11-04): pickable patterns and
    // textures from utils/backgroundDesigns.ts.
    'lattice', 'wave_ribbon', 'harlequin', 'pinstripe', 'dot_grid',
    'contour', 'starburst', 'basket_weave', 'chevron', 'fan_scallop',
    'marble_vein', 'granite_speckle', 'herringbone', 'satin_sheen'
] as const;

export const AI_FONT_IDS = ['times', 'arial', 'courier', 'garamond', 'georgia', 'helvetica'] as const;

export const AI_PAPER_COLOR_IDS = [
    'cream', 'white', 'ivory', 'light_blue', 'light_green', 'light_pink',
    'antique', 'lavender', 'peach', 'treasury_green'
] as const;

export const AI_SECURITY_FEATURE_IDS = [
    'microprint', 'guilloche', 'void_pantograph', 'watermark', 'uv_markers',
    'holographic', 'chemical_reactive', 'thermochromic', 'warning_band',
    'anti_copy_vdot', 'prismatic_column', 'microprint_sig', 'cpsa_padlock',
    'micr_clear_band'
] as const;

const PAPER_HEX_TO_ID: Record<string, string> = {
    '#f8f4ec': 'cream',
    '#ffffff': 'white',
    '#fffff0': 'ivory',
    '#e8f4fc': 'light_blue',
    '#f0f8f0': 'light_green',
    '#fff0f5': 'light_pink',
    '#faebd7': 'antique',
    '#f5f0ff': 'lavender',
    '#fff5ee': 'peach',
    '#e8f0e0': 'treasury_green'
};

const COLOR_HEX: Record<string, string> = {
    'navy blue': '#0b2a5b',
    'royal blue': '#1d4ed8',
    'sky blue': '#38bdf8',
    'light blue': '#38bdf8',
    'dark blue': '#1e3a8a',
    blue: '#2563eb',
    cyan: '#06b6d4',
    teal: '#0f766e',
    emerald: '#059669',
    'dark green': '#14532d',
    green: '#15803d',
    lime: '#65a30d',
    olive: '#4d7c0f',
    yellow: '#ca8a04',
    gold: '#c9a227',
    orange: '#ea580c',
    'dark red': '#7f1d1d',
    red: '#dc2626',
    maroon: '#7f1d1d',
    burgundy: '#722f37',
    pink: '#db2777',
    magenta: '#c026d3',
    purple: '#7e22ce',
    violet: '#7c3aed',
    indigo: '#4338ca',
    black: '#111827',
    charcoal: '#374151',
    grey: '#6b7280',
    gray: '#6b7280',
    silver: '#9ca3af',
    white: '#ffffff',
    cream: '#fffdd0',
    beige: '#d6c7a8',
    brown: '#92400e',
    tan: '#b45309'
};

const PAPER_BY_COLOR: Record<string, string> = {
    cream: 'cream',
    white: 'white',
    ivory: 'ivory',
    blue: 'light_blue',
    'light blue': 'light_blue',
    'sky blue': 'light_blue',
    'safety blue': 'light_blue',
    green: 'light_green',
    'light green': 'light_green',
    sage: 'light_green',
    pink: 'light_pink',
    'light pink': 'light_pink',
    blush: 'light_pink',
    gold: 'antique',
    antique: 'antique',
    beige: 'antique',
    lavender: 'lavender',
    peach: 'peach',
    'treasury green': 'treasury_green'
};

interface BankMatch {
    pattern: RegExp;
    id: string | ((prompt: string) => string);
    name: string | ((prompt: string) => string);
    style?: string | ((prompt: string) => string | undefined);
}

const BANK_MATCHES: BankMatch[] = [
    { pattern: /\bchase private(?: client)?\b/, id: 'CHASE_PRIVATE', name: 'Chase Private Client' },
    { pattern: /\bchase\b/, id: 'CHASE', name: 'Chase Business Elite' },
    {
        pattern: /\bbank of america\b|\bbofa\b/,
        id: (p) => /\bclassic\b/.test(p) ? 'BOFA_CLASSIC' : (/\bv2\b|business v2/.test(p) ? 'BOFA_BUSINESS_V2' : 'BOFA'),
        name: (p) => /\bclassic\b/.test(p) ? 'Bank of America Classic' : (/\bv2\b|business v2/.test(p) ? 'Bank of America Business (v2)' : 'Bank of America Premium Business'),
        style: (p) => /\bclassic\b/.test(p) ? 'bofa_classic' : (/\bv2\b|business v2/.test(p) ? 'bofa_v2' : undefined)
    },
    { pattern: /\bwells fargo\b/, id: 'WELLS', name: 'Wells Fargo Executive' },
    { pattern: /\bcitibank\b|\bciti\b/, id: 'CITI', name: 'CitiBusiness Professional' },
    { pattern: /\bnavy federal\b/, id: 'NAVY_FCU', name: 'Navy Federal Credit Union' },
    { pattern: /\busaa\b/, id: 'USAA', name: 'USAA Premium Personal' },
    { pattern: /\bpnc\b/, id: 'PNC', name: 'PNC Business Elite' },
    { pattern: /\btd bank\b|\btd\b/, id: 'TD', name: 'TD Bank Professional' },
    { pattern: /\bcapital one\b/, id: 'CAPITAL_ONE', name: 'Capital One Premium' },
    { pattern: /\bu\.?s\.? bank\b|\bus bank\b/, id: 'US_BANK', name: 'US Bank Commercial' },
    { pattern: /\bhuntington\b/, id: 'HUNTINGTON', name: 'Huntington Business' },
    { pattern: /\bfifth third\b/, id: 'FIFTH_THIRD', name: 'Fifth Third Bank' },
    { pattern: /\bkeybank\b/, id: 'KEYBANK', name: 'KeyBank Business' },
    { pattern: /\bcitizens bank\b|\bcitizens\b/, id: 'CITIZENS', name: 'Citizens Bank' },
    { pattern: /\bally bank\b|\bally\b/, id: 'ALLY', name: 'Ally Bank Digital' },
    { pattern: /\bgreenfi\b/, id: 'GREENFI', name: 'GreenFi' },
    { pattern: /\banb\b/, id: 'ANB', name: 'ANB Bank' },
    { pattern: /\bdiscover bank\b|\bdiscover\b/, id: 'DISCOVER', name: 'Discover Bank' },
    { pattern: /\bregions bank\b|\bregions\b/, id: 'REGIONS', name: 'Regions Bank' },
    { pattern: /\btruist\b/, id: 'TRUIST', name: 'Truist' },
    { pattern: /\bbmo\b/, id: 'BMO', name: 'BMO Harris' },
    { pattern: /\bzions\b/, id: 'ZIONS', name: 'Zions Bank' },
    { pattern: /\bcomerica\b/, id: 'COMERICA', name: 'Comerica Bank' },
    { pattern: /\bfirst horizon\b/, id: 'FIRST_HORIZON', name: 'First Horizon' },
    { pattern: /\bm&t bank\b|\bm and t\b/, id: 'M_AND_T', name: 'M&T Bank' },
    { pattern: /\bpenfed\b|\bpentagon federal\b/, id: 'PENFED', name: 'Pentagon FCU' },
    { pattern: /\bbecu\b/, id: 'BECU', name: 'BECU' },
    { pattern: /\balliant\b/, id: 'ALLIANT', name: 'Alliant Credit Union' },
    { pattern: /\bu\.?s\.? treasury\b|\bunited states treasury\b|\btreasury check\b/, id: 'US_TREASURY_OFFICIAL', name: 'United States Treasury', style: 'us_treasury' },
    { pattern: /\bkaren art studio\b/, id: 'KAREN_ART_STUDIO_PAYROLL', name: 'Karen Art Studio Payroll', style: 'payroll_karen' },
    { pattern: /\bclassic elegant\b/, id: 'classic_elegant', name: 'Classic Elegant' },
    { pattern: /\bexecutive gold\b/, id: 'executive_gold', name: 'Executive Gold' },
    { pattern: /\broyal blue\b/, id: 'royal_blue', name: 'Royal Blue' },
    { pattern: /\bcorporate (?:grey|gray)\b/, id: 'corporate_grey', name: 'Corporate Grey' },
    { pattern: /\bemerald prestige\b/, id: 'emerald_prestige', name: 'Emerald Prestige' },
    { pattern: /\bburgundy classic\b/, id: 'burgundy_classic', name: 'Burgundy Classic' },
    { pattern: /\bmidnight premium\b/, id: 'midnight_premium', name: 'Midnight Premium' },
    { pattern: /\bbronze heritage\b/, id: 'bronze_heritage', name: 'Bronze Heritage' },
    { pattern: /\bultra obsidian\b/, id: 'ultra_obsidian', name: 'Ultra Prestige Obsidian' },
    { pattern: /\bultra sapphire\b/, id: 'ultra_sapphire', name: 'Ultra Prestige Sapphire' },
    { pattern: /\bultra amethyst\b/, id: 'ultra_amethyst', name: 'Ultra Prestige Amethyst' },
    { pattern: /\bultra crimson\b/, id: 'ultra_crimson', name: 'Ultra Prestige Crimson' },
    { pattern: /\bultra diamond\b/, id: 'ultra_diamond', name: 'Ultra Prestige Diamond' },
    { pattern: /\bcorporate safety\b/, id: 'corporate_safety', name: 'Corporate Safety' },
    { pattern: /\bchime\b/, id: 'chime_pro', name: 'Chime / Digital Pro' },
    { pattern: /\bcredit union classic\b/, id: 'credit_union_classic', name: 'Credit Union Classic' },
    { pattern: /\bvaro\b/, id: 'varo_style', name: 'Varo / Modern Neo' },
    { pattern: /\bprepaid gold\b/, id: 'prepaid_gold', name: 'Prepaid Gold Card' }
];

const TEXTURE_MATCHES: Array<{ pattern: RegExp; id: string; label: string }> = [
    // Design-library expansion (2026-11-04) — specific names first,
    // before the generic fallbacks below can claim their words
    // ("marble", "waves", "diamond").
    { pattern: /\bveined marble\b|\bmarble vein\b/, id: 'marble_vein', label: 'Veined Marble' },
    { pattern: /\bribbon waves?\b|\bwave ribbons?\b/, id: 'wave_ribbon', label: 'Ribbon Waves' },
    { pattern: /\bharlequin\b/, id: 'harlequin', label: 'Harlequin Diamonds' },
    { pattern: /\blattice\b/, id: 'lattice', label: 'Geometric Lattice' },
    { pattern: /\bpinstripe\b/, id: 'pinstripe', label: 'Banker Pinstripe' },
    { pattern: /\bdot (?:grid|matrix)\b|\bhalftone\b/, id: 'dot_grid', label: 'Halftone Dot Grid' },
    { pattern: /\bcontour(?: lines?)?\b|\btopographic\b/, id: 'contour', label: 'Contour Lines' },
    { pattern: /\bstarburst\b|\bsunburst\b/, id: 'starburst', label: 'Starburst Rays' },
    { pattern: /\bbasket ?weave\b/, id: 'basket_weave', label: 'Basket Weave' },
    { pattern: /\bchevron\b/, id: 'chevron', label: 'Chevron Bands' },
    { pattern: /\bscallop fans?\b|\bfan scallop\b|\bfish ?scale\b/, id: 'fan_scallop', label: 'Scallop Fans' },
    { pattern: /\bgranite\b|\bspeckle\b/, id: 'granite_speckle', label: 'Granite Speckle' },
    { pattern: /\bherringbone\b/, id: 'herringbone', label: 'Herringbone Weave' },
    { pattern: /\bsatin\b/, id: 'satin_sheen', label: 'Satin Sheen' },
    { pattern: /\bcloud marble\b/, id: 'cloud_marble', label: 'Classic Cloud Marble' },
    { pattern: /\bcircuit(?: board)?\b|\bcyber\b/, id: 'circuit_board', label: 'FinTech Circuitry' },
    { pattern: /\bdigital hive\b|\bhex(?:agon)?\b|\bhoneycomb\b/, id: 'digital_hive', label: 'Digital Hive' },
    { pattern: /\bgeometric(?: diamond)?\b|\bdiamond pattern\b/, id: 'geometric_diamond', label: 'Geometric Diamond' },
    { pattern: /\broyal damask\b|\bdamask\b/, id: 'royal_damask', label: 'Royal Damask' },
    { pattern: /\bfluorescent fibers?\b/, id: 'fluorescent_fibers', label: 'Fluorescent Fibers' },
    { pattern: /\bfiber optic\b/, id: 'fiber_optic', label: 'Fiber Optic Security' },
    { pattern: /\blaser security\b|\bmoir[eé]\b/, id: 'laser_security', label: 'Laser Security Moiré' },
    { pattern: /\btoner adhesion\b/, id: 'toner_adhesion', label: 'Toner Adhesion Grain' },
    { pattern: /\bnano[ -]?optical\b/, id: 'nano_optical', label: 'Nano-Optical Screen' },
    { pattern: /\binterference pattern\b/, id: 'interference', label: 'Interference Pattern' },
    { pattern: /\bmulti[ -]?tone\b/, id: 'multi_tone', label: 'Multi-Tone Depth' },
    { pattern: /\bmicro[ -]?embossed\b/, id: 'micro_embossed', label: 'Micro-Embossed Vellum' },
    { pattern: /\bguilloch[eé](?: background| wave| pattern)?\b|\bspirograph\b/, id: 'guilloche_bg', label: 'Guilloché Wave' },
    { pattern: /\bblue wave\b|\bocean\b|\bsea wave\b|\bwaves?\b/, id: 'blue_wave', label: 'Blue Wave' },
    { pattern: /\bprismatic\b|\bprism\b|\brainbow texture\b/, id: 'prismatic', label: 'Prismatic Security' },
    { pattern: /\bsecurity crosshatch\b|\bcrosshatch\b|\bsecurity texture\b/, id: 'security', label: 'Security Crosshatch' },
    { pattern: /\bparticles?\b|\bdust\b/, id: 'particles', label: 'Premium Particles' },
    { pattern: /\bparchment\b/, id: 'parchment', label: 'Parchment' },
    { pattern: /\bmarble\b|\bstone texture\b/, id: 'marble', label: 'Marble Swirl' },
    { pattern: /\bcotton(?: fiber)?\b/, id: 'cotton', label: 'Cotton Fiber' },
    { pattern: /\brough paper\b/, id: 'rough', label: 'Rough Paper' },
    { pattern: /\bplain(?: paper| texture)?\b/, id: 'plain', label: 'Plain Paper' },
    { pattern: /\blinen\b|\bcloth\b|\bfabric texture\b/, id: 'linen', label: 'Linen Weave' }
];

interface SecurityDefinition {
    id: string;
    label: string;
    aliases: string[];
}

const SECURITY_DEFINITIONS: SecurityDefinition[] = [
    { id: 'microprint_sig', label: 'Microprint Signature Line', aliases: ['microprint signature line', 'microprinted signature line'] },
    { id: 'micr_clear_band', label: 'MICR Clear Band Protection', aliases: ['micr clear band protection', 'micr clear-band protection', 'clear band protection'] },
    { id: 'anti_copy_vdot', label: 'Anti-Copy V-Dot', aliases: ['anti-copy v-dot', 'anti copy vdot', 'anti-copy vdot', 'v-dot', 'vdot'] },
    { id: 'void_pantograph', label: 'VOID Pantograph', aliases: ['void pantograph', 'void pattern', 'void'] },
    { id: 'chemical_reactive', label: 'Chemical Reactive Paper', aliases: ['chemical reactive', 'chemical sensitive', 'chemical sensitivity'] },
    { id: 'thermochromic', label: 'Thermochromic Ink', aliases: ['thermochromic', 'heat sensitive ink', 'temperature sensitive ink'] },
    { id: 'prismatic_column', label: 'Prismatic Security Column', aliases: ['prismatic security column', 'prismatic column'] },
    { id: 'cpsa_padlock', label: 'CPSA Padlock Icon', aliases: ['cpsa padlock', 'padlock icon', 'padlock'] },
    { id: 'warning_band', label: 'Security Warning Band', aliases: ['security warning band', 'warning band'] },
    { id: 'holographic', label: 'Holographic Strip', aliases: ['holographic strip', 'holographic', 'hologram'] },
    { id: 'uv_markers', label: 'UV Security Markers', aliases: ['uv security markers', 'uv markers', 'ultraviolet markers', 'uv ink'] },
    { id: 'microprint', label: 'Microprint Border', aliases: ['microprint border', 'microprinting', 'microprint', 'fine print security'] },
    { id: 'guilloche', label: 'Guilloche Pattern', aliases: ['guilloche pattern', 'guilloche security', 'guilloche'] },
    { id: 'watermark', label: 'Watermark', aliases: ['watermark'] }
];

const OBJECT_DEFINITIONS: Array<{ id: string; label: string; aliases: string[]; width: number }> = [
    { id: 'customLogo', label: 'logo', aliases: ['uploaded logo', 'bank logo', 'logo'], width: 120 },
    { id: 'customSignature', label: 'signature', aliases: ['uploaded signature', 'signature image', 'signature'], width: 220 },
    { id: 'bankDetails', label: 'bank details', aliases: ['bank details', 'bank block', 'bank name'], width: 300 },
    { id: 'accHolderName', label: 'account holder name', aliases: ['account holder name', 'holder name'], width: 260 },
    { id: 'accHolderAddr', label: 'account holder address', aliases: ['account holder address', 'holder address'], width: 300 },
    { id: 'dateValue', label: 'date', aliases: ['date field', 'date'], width: 110 },
    { id: 'amountBox', label: 'amount box', aliases: ['amount box', 'numeric amount box'], width: 150 },
    { id: 'payeeNameVal', label: 'payee name', aliases: ['payee name', 'payee'], width: 360 },
    { id: 'amountWordsValue', label: 'written amount', aliases: ['amount words', 'written amount'], width: 560 },
    { id: 'memoValue', label: 'memo', aliases: ['memo field', 'memo'], width: 220 },
    { id: 'signatureLine', label: 'signature line', aliases: ['signature line', 'signature'], width: 300 },
    { id: 'checkNumber', label: 'check number', aliases: ['check number'], width: 90 },
    { id: 'fractionalRouting', label: 'fractional routing', aliases: ['fractional routing'], width: 110 },
    { id: 'watermark', label: 'watermark', aliases: ['watermark'], width: 260 },
    { id: 'holoGroup', label: 'holographic strip', aliases: ['holographic strip', 'hologram'], width: 60 },
    { id: 'thermoSeal', label: 'thermochromic seal', aliases: ['thermochromic seal', 'thermal seal'], width: 60 },
    { id: 'padlockGroup', label: 'padlock', aliases: ['cpsa padlock', 'padlock'], width: 20 },
    { id: 'padlockStatement', label: 'padlock statement', aliases: ['security features statement', 'details on back statement'], width: 170 },
    { id: 'warning_band', label: 'warning band', aliases: ['warning band'], width: 300 }
];

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
const cleanValue = (value: string): string => value
    .replace(/\s+/g, ' ')
    .replace(/^(?:the|a|an)\s+/i, '')
    .replace(/\s+(?:please|thanks|thank you)$/i, '')
    .replace(/[.!?]+$/, '')
    .trim();

function firstCapture(raw: string, patterns: RegExp[]): string | undefined {
    for (const pattern of patterns) {
        const match = raw.match(pattern);
        if (match?.[1]) return cleanValue(match[1]);
    }
    return undefined;
}

function isLocationOnly(value: string | undefined): boolean {
    return Boolean(value && /^(?:top|bottom|center|centre|left|right|up|down)(?:\s+(?:left|right))?\b/.test(value.trim().toLowerCase()));
}

function normalizePaper(value: string | undefined): string | undefined {
    if (!value) return undefined;
    if ((AI_PAPER_COLOR_IDS as readonly string[]).includes(value)) return value;
    return PAPER_HEX_TO_ID[value.toLowerCase()];
}

function defaultPosition(id: string, width: number, height: number): AiObjectMove {
    const map: Record<string, AiObjectMove> = {
        customLogo: { left: 50, top: 50, scaleX: 0.5, scaleY: 0.5 },
        customSignature: { left: width - 350, top: height - 108, scaleX: 1, scaleY: 1 },
        bankDetails: { left: 40, top: 12 },
        accHolderName: { left: 40, top: 62 },
        accHolderAddr: { left: 40, top: 80 },
        dateValue: { left: width - 165, top: 50 },
        amountBox: { left: width - 150, top: 106 },
        payeeNameVal: { left: 110, top: 118 },
        amountWordsValue: { left: 40, top: 154 },
        memoValue: { left: 90, top: height - 88 },
        signatureLine: { left: width - 350, top: height - 68 },
        checkNumber: { left: width - 100, top: 10 },
        fractionalRouting: { left: width - 100, top: 31 },
        watermark: { left: width / 2, top: height / 2 },
        holoGroup: { left: width - 60, top: 20 },
        thermoSeal: { left: width - 70, top: 175 },
        padlockGroup: { left: width - 102, top: 153 },
        padlockStatement: { left: width - 546, top: height - 80 },
        warning_band: { left: 45, top: height - 106 }
    };
    return map[id] || { left: 40, top: 40, scaleX: 1, scaleY: 1 };
}

/**
 * Deposit-style intent, matched on the prompt's deposit phrasing alone —
 * "set the deposit style to business", "change deposit to mobile",
 * "mobile deposit". This is detected up front because the bare word
 * "business" in a deposit phrase used to be claimed by the front-layout
 * matcher, which changed the check's face and left the back untouched.
 */
function matchDepositStyle(p: string): string | undefined {
    const m = p.match(/\bdeposit\s+(?:style|type|block|endorsement)?\s*(?:to|:|=)?\s*(mobile|atm|business|standard)\b/)
        || p.match(/\b(mobile|atm|business|standard)\s+deposit\b/);
    return m?.[1];
}

export const processAiPrompt = async (
    prompt: string,
    current?: AiStudioState
): Promise<AiDesignResult> => {
    const raw = prompt.trim();
    const p = raw.toLowerCase().replace(/[’]/g, "'");
    const width = current?.width || 850;
    const height = current?.height || 350;
    const changes: string[] = [];
    const warnings: string[] = [];
    const changeKeys = new Set<string>();
    const addChange = (key: string, label: string) => {
        if (changeKeys.has(key)) return;
        changeKeys.add(key);
        changes.push(label);
    };
    const addWarning = (warning: string) => {
        if (!warnings.includes(warning)) warnings.push(warning);
    };

    const result: AiDesignResult = {
        styles: {},
        fields: {},
        border: {},
        controls: {},
        layout: { moveMap: {} },
        security: { enable: [], disable: [], features: [] },
        meta: { explanation: '', changes, warnings, confidence: 0 }
    };

    const depositStyleIntent = matchDepositStyle(p);

    const setStyle = <K extends keyof AiDesignResult['styles']>(
        key: K,
        value: NonNullable<AiDesignResult['styles'][K]>,
        currentValue: unknown,
        label: string
    ) => {
        const normalizedCurrent = key === 'paperColor' ? normalizePaper(String(currentValue || '')) : currentValue;
        if (normalizedCurrent === value) return;
        result.styles[key] = value;
        addChange(`style:${String(key)}`, label);
    };
    const setField = <K extends keyof AiCheckFields>(
        key: K,
        value: AiCheckFields[K],
        label: string
    ) => {
        const currentValue = current?.checkData?.[key];
        if (currentValue === value) return;
        result.fields[key] = value;
        addChange(`field:${String(key)}`, label);
    };
    const setControl = <K extends keyof AiDesignResult['controls']>(
        key: K,
        value: NonNullable<AiDesignResult['controls'][K]>,
        currentValue: unknown,
        label: string
    ) => {
        if (currentValue === value) return;
        result.controls[key] = value;
        addChange(`control:${String(key)}`, label);
    };
    const setBorder = <K extends keyof AiBorderPatch>(
        key: K,
        value: NonNullable<AiBorderPatch[K]>,
        label: string
    ) => {
        if (current?.borderConfig?.[key] === value) return;
        result.border[key] = value;
        if (key === 'style') result.styles.borderStyle = value as BorderStyle;
        if (key === 'color') {
            result.styles.borderColor = value as string;
            result.styles.primaryColor = value as string;
        }
        if (key === 'secondaryColor') result.styles.secondaryColor = value as string;
        addChange(`border:${String(key)}`, label);
    };

    // ---- Bank / premium template -------------------------------------------------
    let bankHandledStyle: string | undefined;
    for (const bank of BANK_MATCHES) {
        if (!bank.pattern.test(p)) continue;
        const id = typeof bank.id === 'function' ? bank.id(p) : bank.id;
        const name = typeof bank.name === 'function' ? bank.name(p) : bank.name;
        const style = typeof bank.style === 'function' ? bank.style(p) : bank.style;
        result.meta.bankDetected = id;
        result.meta.bankName = name;
        addChange(`bank:${id}`, `Selected the ${name} template`);
        if (style) {
            bankHandledStyle = style;
            setStyle('checkStyle', style, current?.checkStyle, `Applied the ${name} check layout`);
        }
        break;
    }

    // ---- Check type --------------------------------------------------------------
    const styleMatches: Array<{ pattern: RegExp; id: string; label: string }> = [
        { pattern: /\bcashier(?:'s)? check\b/, id: 'cashier', label: "Cashier's Check" },
        { pattern: /\bmoney order\b/, id: 'money_order', label: 'Money Order' },
        { pattern: /\bcertified check\b/, id: 'certified', label: 'Certified Check' },
        { pattern: /\bcounter check\b/, id: 'counter', label: 'Counter Check' },
        { pattern: /\be[ -]?check\b|\bdigital check\b/, id: 'echeck', label: 'eCheck Digital' },
        { pattern: /\bpayroll classic\b|\bclassic payroll\b/, id: 'payroll_classic', label: 'Payroll Check (Classic)' },
        { pattern: /\bpayroll\b/, id: 'payroll', label: 'Payroll Check' },
        { pattern: /\bbusiness check\b|\bcorporate check\b|\bcompany check\b|\bbusiness\b|\bcorporate\b/, id: 'business', label: 'Business Check' },
        { pattern: /\bpersonal check\b|\bpersonal\b/, id: 'personal', label: 'Personal Check' }
    ];
    if (!bankHandledStyle) {
        for (const style of styleMatches) {
            // A deposit phrase ("deposit style to business") is a back
            // instruction, not a front-layout one — never let its bare
            // "business" word switch the check layout.
            if (style.id === 'business' && depositStyleIntent === 'business'
                && !/\bbusiness check\b|\bcorporate check\b|\bcompany check\b/.test(p)) continue;
            if (style.pattern.test(p)) {
                setStyle('checkStyle', style.id, current?.checkStyle, `Set the layout to ${style.label}`);
                break;
            }
        }
    }

    // ---- Direct check fields ------------------------------------------------------
    const payee = firstCapture(raw, [
        /\bpay(?:ee)?(?:\s+name)?\s+(?:to|is|:|=)\s+([^,;\n$]+?)(?=\s+(?:\$[0-9]|(?:for|amount|memo|on)(?:\s|$))|$)/i,
        /\bmake\s+(?:it\s+)?out\s+to\s+([^,;\n$]+?)(?=\s+(?:\$[0-9]|(?:for|amount|memo|on)(?:\s|$))|$)/i,
        /\bpayable\s+to\s+([^,;\n$]+?)(?=\s+(?:\$[0-9]|(?:for|amount|memo|on)(?:\s|$))|$)/i,
        /\bpay\s+to\s+([^,;\n$]+?)(?=\s+(?:\$[0-9]|(?:for|amount|memo|on)(?:\s|$))|$)/i
    ]);
    if (payee && !isLocationOnly(payee)) setField('payeeName', payee, `Set payee to ${payee}`);

    const clearAmount = /\b(?:clear|remove|blank|erase|delete)\s+(?:the\s+)?amount\b/i.test(raw)
        || /\bamount\s+(?:blank|empty|cleared)\b/i.test(raw)
        || /\bblank\s+check\b/i.test(raw);
    if (clearAmount) {
        setField('amount', 0, 'Cleared the amount - the amount box and written line stay blank until an amount is added');
    }

    const amountMatch = raw.match(/\$\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/)
        || raw.match(/\bamount\s*(?:of|to|is|:|=)?\s*\$?\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)\s*(?:dollars?|usd)?\b/i);
    if (amountMatch && !clearAmount) {
        const amount = Number(amountMatch[1].replace(/,/g, ''));
        if (Number.isFinite(amount) && amount > 0) {
            setField('amount', amount, `Set the amount to $${amount.toFixed(2)}`);
        } else {
            addWarning('The amount in the prompt was not a valid positive number, so it was not applied.');
        }
    }

    const memo = firstCapture(raw, [
        /\bmemo\s*(?:says|reads|is|to|:|=)?\s*["']([^"']+)["']/i,
        /\bmemo\s*(?:says|reads|is|to|:|=)\s+([^,;\n]+)/i
    ]);
    if (memo && !isLocationOnly(memo)) setField('memo', memo, `Set the memo to “${memo}”`);
    else if (payee && amountMatch) {
        const forMemo = raw.match(/\bfor\s+([^,;\n]+?)(?=\s*$)/i)?.[1];
        if (forMemo && !/security|texture|border|paper|deposit/i.test(forMemo)) {
            const value = cleanValue(forMemo);
            if (value) setField('memo', value, `Set the memo to “${value}”`);
        }
    }

    const bankName = firstCapture(raw, [/\bbank\s+name\s*(?:to|is|:|=)\s+([^,;\n]+)/i]);
    if (bankName && !isLocationOnly(bankName) && !result.meta.bankDetected) setField('bankName', bankName, `Set the bank name to ${bankName}`);
    const bankAddress = firstCapture(raw, [/\bbank\s+address\s*(?:to|is|:|=)\s+([^,;\n]+)/i]);
    if (bankAddress) setField('bankAddress', bankAddress, 'Updated the bank address');
    const holder = firstCapture(raw, [
        /\baccount\s+holder(?:\s+name)?\s*(?:to|is|:|=)\s+([^,;\n]+)/i,
        /\bholder\s+name\s*(?:to|is|:|=)\s+([^,;\n]+)/i
    ]);
    if (holder && !isLocationOnly(holder)) setField('accountHolderName', holder, `Set the account holder to ${holder}`);
    const holderAddress = firstCapture(raw, [/\baccount\s+holder\s+address\s*(?:to|is|:|=)\s+([^,;\n]+)/i]);
    if (holderAddress) setField('accountHolderAddress', holderAddress, 'Updated the account holder address');
    const payeeAddress = firstCapture(raw, [/\bpayee\s+address\s*(?:to|is|:|=)\s+([^,;\n]+)/i]);
    if (payeeAddress) setField('payeeAddress', payeeAddress, 'Updated the payee address');
    const watermarkText = firstCapture(raw, [/\bwatermark\s+text\s*(?:to|is|:|=)\s*["']?([^,;\n"']+)["']?/i]);
    if (watermarkText && !isLocationOnly(watermarkText)) setField('watermarkText', watermarkText.toUpperCase(), `Set the watermark text to ${watermarkText.toUpperCase()}`);

    const routing = raw.match(/\brouting(?:\s+number|\s+no\.?|\s+#)?\s*(?:to|is|:|=)?\s*([0-9]{9})\b/i)?.[1];
    if (routing) setField('routingNumber', routing, `Set the routing number to ${routing}`);
    const account = raw.match(/\baccount(?:\s+number|\s+no\.?|\s+#)?\s*(?:to|is|:|=)?\s*([0-9][0-9\s-]{3,24}[0-9])\b/i)?.[1]?.replace(/[\s-]/g, '');
    if (account) setField('accountNumber', account, `Set the account number to ${account}`);
    const checkNumber = raw.match(/\bcheck(?:\s+number|\s+no\.?|\s+#)?\s*(?:to|is|:|=)?\s*([0-9]{1,10})\b/i)?.[1];
    if (checkNumber) setField('checkNumber', checkNumber, `Set the check number to ${checkNumber}`);
    const fractional = raw.match(/\bfractional\s+routing\s*(?:to|is|:|=)?\s*([0-9]{1,4}\s*\/\s*[0-9]{1,6})/i)?.[1]?.replace(/\s/g, '');
    if (fractional) setField('fractionalRouting', fractional, `Set fractional routing to ${fractional}`);
    const epc = raw.match(/\bepc(?:\s+digit)?\s*(?:to|is|:|=)?\s*([0-9])\b/i)?.[1];
    if (epc) setField('epcDigit', epc, `Set the EPC digit to ${epc}`);
    const ein = raw.match(/\bein\s*(?:to|is|:|=)?\s*([0-9]{2}-[0-9]{7})\b/i)?.[1];
    if (ein) setField('ein', ein, `Set the EIN to ${ein}`);
    const lastFour = raw.match(/\blast\s*(?:four|4)\s*(?:to|is|:|=)?\s*([0-9]{4})\b/i)?.[1];
    if (lastFour) setField('lastFour', lastFour, `Set the filename last-four to ${lastFour}`);

    const numericDate = raw.match(/\bdate\s*(?:to|is|:|=)?\s*([0-9]{1,2})[/-]([0-9]{1,2})[/-]([0-9]{2,4})\b/i);
    if (numericDate) {
        const year = numericDate[3].length === 2 ? `20${numericDate[3]}` : numericDate[3];
        const date = `${numericDate[1].padStart(2, '0')}/${numericDate[2].padStart(2, '0')}/${year}`;
        setField('date', date, `Set the date to ${date}`);
    } else {
        const monthNames: Record<string, string> = {
            january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
            july: '07', august: '08', september: '09', october: '10', november: '11', december: '12'
        };
        const named = raw.match(/\bdate\s*(?:to|is|:|=)?\s*(january|february|march|april|may|june|july|august|september|october|november|december)\s+([0-9]{1,2}),?\s+([0-9]{4})\b/i);
        if (named) {
            const date = `${monthNames[named[1].toLowerCase()]}/${named[2].padStart(2, '0')}/${named[3]}`;
            setField('date', date, `Set the date to ${date}`);
        }
    }

    const payrollFields: Array<{ pattern: RegExp; key: keyof AiCheckFields; label: string }> = [
        { pattern: /\bhours\s+worked\s*(?:to|is|:|=)?\s*([0-9]+(?:\.[0-9]+)?)/i, key: 'hoursWorked', label: 'hours worked' },
        { pattern: /\b(?:hourly\s+rate|pay\s+rate)\s*(?:to|is|:|=)?\s*\$?\s*([0-9]+(?:\.[0-9]+)?)/i, key: 'payRate', label: 'pay rate' },
        { pattern: /\b(?:pay\s+total|gross\s+pay)\s*(?:to|is|:|=)?\s*\$?\s*([0-9,]+(?:\.[0-9]+)?)/i, key: 'payTotal', label: 'pay total' },
        { pattern: /\bytd\s+gross\s*(?:to|is|:|=)?\s*\$?\s*([0-9,]+(?:\.[0-9]+)?)/i, key: 'ytdGross', label: 'YTD gross' },
        { pattern: /\bytd\s+net\s*(?:to|is|:|=)?\s*\$?\s*([0-9,]+(?:\.[0-9]+)?)/i, key: 'ytdNet', label: 'YTD net' }
    ];
    for (const field of payrollFields) {
        const value = raw.match(field.pattern)?.[1];
        if (value) setField(field.key, value, `Set ${field.label} to ${value}`);
    }
    const earnings = raw.match(/\bearnings\s+type\s*(?:to|is|:|=)?\s*(salary|hourly|commission|bonus)\b/i)?.[1];
    if (earnings) {
        // Earnings type is a checkData extension in CheckBuilder's payroll form.
        (result.fields as Record<string, unknown>).earningsType = earnings.toUpperCase();
        addChange('field:earningsType', `Set earnings type to ${earnings.toUpperCase()}`);
    }

    // ---- Texture, paper, typography ---------------------------------------------
    for (const texture of TEXTURE_MATCHES) {
        if (texture.pattern.test(p)) {
            setStyle('texture', texture.id, current?.selectedTexture, `Applied the ${texture.label} texture`);
            break;
        }
    }
    if (/\bwood(?:en)?\s+(?:grain|texture)\b/.test(p)) {
        addWarning('The studio has no wood-grain texture control, so that texture was not applied.');
    }
    if (/\beagle shield\b/.test(p)) {
        addWarning('The studio has no eagle-shield texture control, so that texture was not applied.');
    }

    const texturePercent = p.match(/\btexture\s*(?:at|to|opacity)?\s*([0-9]{1,3})\s*%/);
    if (texturePercent) {
        const opacity = clamp(Number(texturePercent[1]) / 100, 0, 1);
        setStyle('textureOpacity', opacity, current?.textureOpacity, `Set texture intensity to ${Math.round(opacity * 100)}%`);
    } else if (/\bsubtle texture\b|\blight texture\b/.test(p)) {
        setStyle('textureOpacity', 0.45, current?.textureOpacity, 'Set a subtle texture intensity (45%)');
    } else if (/\bstrong texture\b|\bbold texture\b/.test(p)) {
        setStyle('textureOpacity', 1, current?.textureOpacity, 'Set a strong texture intensity (100%)');
    }

    const fontMatch = p.match(/\b(times|arial|courier|garamond|georgia|helvetica)\s+(?:font|typeface)\b/)
        || p.match(/\bfont\s*(?:to|is|:)?\s*(times|arial|courier|garamond|georgia|helvetica)\b/);
    if (fontMatch) {
        const labels: Record<string, string> = {
            times: 'Times', arial: 'Arial', courier: 'Courier', garamond: 'Garamond', georgia: 'Georgia', helvetica: 'Helvetica'
        };
        setStyle('font', fontMatch[1], current?.selectedFont, `Set the primary font to ${labels[fontMatch[1]]}`);
    }

    const payeeSize = raw.match(/\bpayee\s+(?:font\s+)?size\s*(?:to|is|:|=)?\s*([0-9]{1,2})(?:px)?\b/i)?.[1];
    if (payeeSize) setField('payeeFontSize', clamp(Number(payeeSize), 10, 32), `Set payee text to ${clamp(Number(payeeSize), 10, 32)}px`);
    const memoSize = raw.match(/\bmemo\s+(?:font\s+)?size\s*(?:to|is|:|=)?\s*([0-9]{1,2})(?:px)?\b/i)?.[1];
    if (memoSize) setField('memoFontSize', clamp(Number(memoSize), 8, 24), `Set memo text to ${clamp(Number(memoSize), 8, 24)}px`);

    // ---- Colors ------------------------------------------------------------------
    // Paper and ink phrases are handled separately so "blue ink" does not also
    // become a blue border, and "blue paper" does not become a blue theme.
    let themeText = ` ${p} `;
    const inkMatch = themeText.match(/\b(blue|black|red|green|purple|brown)\s+ink\b/);
    if (inkMatch) {
        const inkHex: Record<string, string> = {
            blue: '#003366', black: '#111827', red: '#991b1b', green: '#14532d', purple: '#581c87', brown: '#78350f'
        };
        setControl('handwritingColor', inkHex[inkMatch[1]], current?.handwritingColor, `Set the handwriting ink to ${inkMatch[1]}`);
        themeText = themeText.replace(inkMatch[0], ' ink ');
    }

    const colorNames = Object.keys(COLOR_HEX).sort((a, b) => b.length - a.length);

    // ---- Security medallion (seal) ----------------------------------------------
    // The medallion by the signature line has its own colour + finish
    // state in the studio. Route its clause here, BEFORE the paper /
    // border / generic colour scans, and strip the clause from
    // themeText: without this, "make the seal gold" was stolen by the
    // generic scan, repainted the BORDER gold, and reported a "gold
    // design theme" the user never asked for. `sealTouched` is read
    // again after the security section (the medallion only prints
    // while Thermochromic Ink is on).
    let sealTouched = false;
    const sealIdx = p.search(/\b(?:seal|medallion)\b/);
    if (sealIdx >= 0) {
        const sealWindow = p.slice(Math.max(0, sealIdx - 24), sealIdx + 80);
        if (/\bmetallic\b|\bshiny\b|\bshine\b|\bfoil\b/.test(sealWindow)) {
            setControl('sealFinish', 'metallic', current?.sealFinish, 'Set the medallion finish to metallic shine');
            sealTouched = true;
        } else if (/\bflat\b|\bmatte\b/.test(sealWindow)) {
            setControl('sealFinish', 'flat', current?.sealFinish, 'Set the medallion finish to flat');
            sealTouched = true;
        }
        const sealHex = sealWindow.match(/#([0-9a-f]{6})\b/i);
        // Studio presets first — their hexes are exactly what the
        // medallion colour picker applies — then any named colour.
        const sealPreset = [...SEAL_COLOR_PRESETS]
            .sort((a, b) => b.name.length - a.name.length)
            .find(pr => new RegExp(`\\b${escapeRegExp(pr.name.toLowerCase())}\\b`).test(sealWindow))
            || [...SEAL_COLOR_PRESETS]
                .sort((a, b) => b.id.length - a.id.length)
                .find(pr => new RegExp(`\\b${escapeRegExp(pr.id)}\\b`).test(sealWindow));
        if (sealHex) {
            setControl('sealColor', `#${sealHex[1].toLowerCase()}`, current?.sealColor, `Set the medallion colour to #${sealHex[1].toLowerCase()}`);
            sealTouched = true;
        } else if (sealPreset) {
            setControl('sealColor', sealPreset.color, current?.sealColor, `Set the medallion colour to ${sealPreset.name}`);
            sealTouched = true;
        } else {
            const sealColour = colorNames.find(n => new RegExp(`\\b${escapeRegExp(n)}\\b`).test(sealWindow));
            if (sealColour) {
                setControl('sealColor', COLOR_HEX[sealColour], current?.sealColor, `Set the medallion colour to ${sealColour}`);
                sealTouched = true;
            }
        }
        themeText = themeText.replace(/[^,;.!?]*\b(?:seal|medallion)\b[^,;.!?]*/g, ' seal ');
    }

    const paperColorNames = `${colorNames.map(escapeRegExp).join('|')}|safety blue|sage|blush|antique|treasury green`;
    const paperColorPattern = new RegExp(
        `\\b(${paperColorNames})\\s+paper\\b|\\bpaper\\s+(?:color\\s+)?(?:to\\s+|with\\s+)?(${paperColorNames})\\b|\\b(${paperColorNames})\\s+background\\b|\\bbackground\\s+(?:color\\s+)?(?:to\\s+|with\\s+)?(${paperColorNames})\\b`
    );
    const paperMatch = themeText.match(paperColorPattern);
    if (paperMatch) {
        const colorName = paperMatch[1] || paperMatch[2] || paperMatch[3] || paperMatch[4];
        const paperId = PAPER_BY_COLOR[colorName];
        if (paperId) {
            const labels: Record<string, string> = {
                cream: 'Classic Cream', white: 'Bright White', ivory: 'Ivory', light_blue: 'Safety Blue',
                light_green: 'Sage Green', light_pink: 'Blush Pink', antique: 'Antique Gold',
                lavender: 'Lavender Mist', peach: 'Peach Glow', treasury_green: 'Treasury Green'
            };
            setStyle('paperColor', paperId, current?.paperColor, `Set the paper to ${labels[paperId]}`);
        } else {
            addWarning(`The paper palette has no ${colorName} paper color, so the paper color was not changed.`);
        }
        themeText = themeText.replace(paperMatch[0], ' paper ');
    }

    const borderColorMatch = themeText.match(new RegExp(`\\b(${colorNames.map(escapeRegExp).join('|')})\\s+border\\b|\\bborder\\s+(?:color\\s+)?(?:to\\s+)?(${colorNames.map(escapeRegExp).join('|')})\\b`));
    if (borderColorMatch) {
        const colorName = borderColorMatch[1] || borderColorMatch[2];
        setBorder('color', COLOR_HEX[colorName], `Set the border color to ${colorName}`);
        themeText = themeText.replace(borderColorMatch[0], ' border ');
    }

    const accentMatch = themeText.match(new RegExp(`\\b(${colorNames.map(escapeRegExp).join('|')})\\s+accents?\\b|\\baccents?\\s+(?:in\\s+|of\\s+)?(${colorNames.map(escapeRegExp).join('|')})\\b`));
    if (accentMatch) {
        const colorName = accentMatch[1] || accentMatch[2];
        setBorder('secondaryColor', COLOR_HEX[colorName], `Set the accent color to ${colorName}`);
        result.styles.accentColor = COLOR_HEX[colorName];
        themeText = themeText.replace(accentMatch[0], ' accent ');
    }

    const explicitHex = themeText.match(/#([0-9a-f]{6})\b/i);
    if (explicitHex) {
        setBorder('color', `#${explicitHex[1].toLowerCase()}`, `Set the border color to #${explicitHex[1].toLowerCase()}`);
    } else if (!borderColorMatch) {
        const found: Array<{ name: string; index: number }> = [];
        for (const name of colorNames) {
            const idx = themeText.search(new RegExp(`\\b${escapeRegExp(name)}\\b`));
            if (idx >= 0) found.push({ name, index: idx });
        }
        found.sort((a, b) => a.index - b.index);
        // Avoid treating the word "gold" in the Executive Gold template name as
        // a second, unrelated color command when it is part of that template.
        const unique = found.filter((item, index) => found.findIndex(other => other.name === item.name) === index);
        if (unique[0] && !paperMatch) {
            setBorder('color', COLOR_HEX[unique[0].name], `Applied a ${unique[0].name} design theme`);
        }
        if (unique[1]) {
            setBorder('secondaryColor', COLOR_HEX[unique[1].name], `Set the secondary color to ${unique[1].name}`);
            result.styles.accentColor = COLOR_HEX[unique[1].name];
        }
    }

    // ---- Border -------------------------------------------------------------------
    const borderMatches: Array<{ pattern: RegExp; style: BorderStyle; label: string }> = [
        { pattern: /\bborderless\b|\bno border\b|\bremove (?:the )?border\b/, style: 'NONE', label: 'no border' },
        // Design-library expansion (2026-11-04) — before BRAIDED,
        // whose pattern also claims the word "rope".
        { pattern: /\brope border\b|\btwisted cord\b|\bcord border\b/, style: 'ROPE', label: 'Rope border' },
        { pattern: /\bbold double rule\b|\bdouble rule bold\b/, style: 'DOUBLE_RULE_BOLD', label: 'Bold Double Rule border' },
        { pattern: /\bdouble rule\b/, style: 'DOUBLE_RULE', label: 'Double Rule border' },
        { pattern: /\bgreek key\b|\bmeander\b/, style: 'GREEK_KEY', label: 'Greek Key border' },
        { pattern: /\bdiamond chain\b/, style: 'DIAMOND_CHAIN', label: 'Diamond Chain border' },
        { pattern: /\bbead(?:ed)? border\b|\bpearl border\b|\bbead rail\b/, style: 'BEAD', label: 'Bead border' },
        { pattern: /\bmicroprint frame\b/, style: 'MICROPRINT_FRAME', label: 'Microprint Frame border' },
        { pattern: /\bmicroprint border\b/, style: 'MICROPRINT', label: 'Microprint border' },
        { pattern: /\bart deco border\b/, style: 'ART_DECO', label: 'Art Deco border' },
        { pattern: /\bguilloche border\b/, style: 'GUILLOCHE', label: 'Guilloche border' },
        { pattern: /\bbraided border\b|\brope border\b|\bbraid(?:ed)?\b|\brope\b/, style: 'BRAIDED', label: 'Braided border' },
        { pattern: /\bscalloped border\b/, style: 'SCALLOPED', label: 'Scalloped border' },
        { pattern: /\bornate border\b/, style: 'ORNATE', label: 'Ornate border' },
        { pattern: /\bdouble border\b|\bdouble[ -]line(?: border)?\b/, style: 'DOUBLE', label: 'Double border' },
        { pattern: /\bdotted border\b/, style: 'DOTTED', label: 'Dotted border' },
        { pattern: /\bfiligree border\b/, style: 'FILIGREE', label: 'Filigree border' },
        { pattern: /\btreasury border\b/, style: 'TREASURY', label: 'Treasury border' },
        { pattern: /\bcommercial border\b/, style: 'COMMERCIAL', label: 'Commercial border' },
        { pattern: /\bmodern border\b|\bclean border\b|\bsimple border\b|\bclassic border\b|\bsolid border\b/, style: 'SOLID', label: 'clean solid border' }
    ];
    for (const border of borderMatches) {
        if (border.pattern.test(p)) {
            setBorder('style', border.style, `Applied the ${border.label}`);
            break;
        }
    }
    if (/\btech border\b|\btechnology border\b/.test(p)) {
        addWarning('The border engine has no “tech” border style, so the border style was left unchanged.');
    }
    const thickness = p.match(/\bborder\s+thickness\s*(?:to|is|:)?\s*([0-9]{1,2})(?:px)?\b/);
    if (thickness) {
        const value = clamp(Number(thickness[1]), 1, 10);
        setBorder('thickness', value, `Set border thickness to ${value}px`);
    } else if (/\bthicker border\b|\bborder thicker\b|\bthicken (?:the )?border\b/.test(p)) {
        const value = clamp((current?.borderConfig?.thickness ?? 2) + 2, 1, 10);
        setBorder('thickness', value, `Made the border thicker (${value}px)`);
    } else if (/\bthinner border\b|\bborder thinner\b/.test(p)) {
        const value = clamp((current?.borderConfig?.thickness ?? 2) - 2, 1, 10);
        setBorder('thickness', value, `Made the border thinner (${value}px)`);
    } else if (/\bthick border\b/.test(p)) setBorder('thickness', 6, 'Set a thick border (6px)');
    else if (/\bthin border\b/.test(p)) setBorder('thickness', 1, 'Set a thin border (1px)');
    const radius = p.match(/\bborder\s+(?:corner\s+)?radius\s*(?:to|is|:)?\s*([0-9]{1,2})(?:px)?\b/);
    if (radius) {
        const value = clamp(Number(radius[1]), 0, 50);
        setBorder('cornerRadius', value, `Set border corner radius to ${value}px`);
    } else if (/\brounded (?:border )?corners\b/.test(p)) setBorder('cornerRadius', 12, 'Rounded the border corners');
    else if (/\bsharp (?:border )?corners\b/.test(p)) setBorder('cornerRadius', 0, 'Set sharp border corners');

    // The prompt asked about the border but nothing border-shaped
    // matched ("change the border", "change border settings"): say
    // plainly that the border was not changed and name exactly which
    // border controls exist, instead of a bare refusal.
    if (/\bborder\b/.test(p)
        && Object.keys(result.border).length === 0
        && !warnings.some(w => /border/i.test(w))) {
        addWarning('The border was not changed. For the border I can set: a style (solid, double, double rule, bold double rule, braided, scalloped, ornate, microprint, microprint frame, guilloche, greek key, rope, bead, diamond chain, art deco, filigree, treasury, commercial, dotted, or none), a colour, the thickness, and rounded corners — tell me which you want.');
    }

    // ---- Design treatments ---------------------------------------------------------
    if (/\bminimal(?:ist)?\b|\bclean design\b|\bmodern design\b/.test(p)) {
        if (!fontMatch) setStyle('font', 'arial', current?.selectedFont, 'Set a clean modern Arial font');
        if (!result.border.style) setBorder('style', 'SOLID', 'Applied a clean solid border');
        if (/\bminimal(?:ist)?\b/.test(p)) {
            setStyle('textureOpacity', Math.min(current?.textureOpacity ?? 1, 0.55), current?.textureOpacity, 'Softened the texture for a minimal finish');
        }
        addChange('treatment:minimal', 'Applied a clean, minimal design treatment');
    }
    if (/\belegant\b|\bluxury\b|\bpremium design\b/.test(p)) {
        if (!fontMatch) setStyle('font', 'garamond', current?.selectedFont, 'Set an elegant Garamond font');
        if (!TEXTURE_MATCHES.some(t => t.pattern.test(p))) {
            setStyle('texture', 'guilloche_bg', current?.selectedTexture, 'Applied the Guilloché Wave texture');
        }
        addChange('treatment:elegant', 'Applied an elegant design treatment');
    }
    if (/\btech\b|\bfuturistic\b|\bdigital design\b/.test(p.replace(/\btech border\b/g, ''))) {
        if (!TEXTURE_MATCHES.some(t => t.pattern.test(p))) {
            setStyle('texture', 'circuit_board', current?.selectedTexture, 'Applied the FinTech Circuitry texture');
        }
        if (!fontMatch) setStyle('font', 'helvetica', current?.selectedFont, 'Set a modern Helvetica font');
        addChange('treatment:tech', 'Applied a technology design treatment');
    }
    if (/\bvintage\b|\bretro\b/.test(p)) {
        if (!TEXTURE_MATCHES.some(t => t.pattern.test(p))) {
            setStyle('texture', 'parchment', current?.selectedTexture, 'Applied the Parchment texture');
        }
        if (!fontMatch) setStyle('font', 'times', current?.selectedFont, 'Set a vintage Times font');
        addChange('treatment:vintage', 'Applied a vintage design treatment');
    }

    // ---- Security -------------------------------------------------------------------
    const securityEnabled = (id: string): boolean => {
        if (!current) return false;
        if (Array.isArray(current.securityFeatures)) {
            return current.securityFeatures.find(f => f.id === id)?.enabled || false;
        }
        return Boolean(current.securityFeatures[id]);
    };
    const enableSecurity = (id: string, label: string) => {
        if (!result.security.enable.includes(id)) result.security.enable.push(id);
        if (!securityEnabled(id)) addChange(`security:on:${id}`, `Enabled ${label}`);
    };
    const disableSecurity = (id: string, label: string) => {
        if (!result.security.disable.includes(id)) result.security.disable.push(id);
        if (securityEnabled(id) || !current) addChange(`security:off:${id}`, `Disabled ${label}`);
    };

    if (/\b(?:remove|disable|turn off|without)\s+all\s+security\b|\bno security features\b/.test(p)) {
        for (const def of SECURITY_DEFINITIONS) disableSecurity(def.id, def.label);
    } else if (/\bmaximum security\b|\bfull security\b|\ball security features\b|\bevery security feature\b/.test(p)) {
        for (const def of SECURITY_DEFINITIONS) enableSecurity(def.id, def.label);
    } else if (/\bminimal security\b/.test(p)) {
        for (const def of SECURITY_DEFINITIONS) {
            if (def.id === 'microprint' || def.id === 'micr_clear_band') enableSecurity(def.id, def.label);
            else disableSecurity(def.id, def.label);
        }
    } else {
        for (const def of SECURITY_DEFINITIONS) {
            const aliases = [...def.aliases].sort((a, b) => b.length - a.length);
            let disabled = false;
            let mentioned = false;
            for (const alias of aliases) {
                const escaped = escapeRegExp(alias);
                if (new RegExp(`\\b(?:remove|disable|turn off|hide|drop|without|no)\\s+(?:the\\s+)?${escaped}\\b`).test(p)
                    || new RegExp(`\\b${escaped}\\s+(?:off|disabled|removed)\\b`).test(p)) {
                    disabled = true;
                    mentioned = true;
                    break;
                }
                if (new RegExp(`\\b${escaped}\\b`).test(p)) mentioned = true;
            }
            if (!mentioned) continue;
            if (disabled) disableSecurity(def.id, def.label);
            else enableSecurity(def.id, def.label);
        }
    }
    result.security.features = [...result.security.enable];

    // The medallion disc prints only while Thermochromic Ink is on, so
    // a seal colour/finish change with the feature off would apply
    // invisibly. Turn the feature on with it and report both changes —
    // unless the same prompt explicitly turned the feature off.
    if (sealTouched && !securityEnabled('thermochromic') && !result.security.disable.includes('thermochromic')) {
        enableSecurity('thermochromic', 'Thermochromic Ink (so the medallion shows)');
        result.security.features = [...result.security.enable];
    }

    // ---- MICR and studio controls ---------------------------------------------------
    if (/\bcmc[ -]?7\b/.test(p)) setField('micrFont', 'cmc7', 'Set the MICR standard to CMC-7');
    else if (/\bocr[ -]?a\b/.test(p)) setField('micrFont', 'ocra', 'Set the MICR standard to OCR-A');
    else if (/\bmodern\s+(?:sans\s+)?micr\b/.test(p)) setField('micrFont', 'modern', 'Set the MICR standard to Modern Sans');
    else if (/\be[ -]?13b\b|\bstandard micr\b/.test(p)) setField('micrFont', 'e13b', 'Set the MICR standard to E-13B');
    const micrSize = raw.match(/\bmicr\s+(?:font\s+)?size\s*(?:to|is|:|=)?\s*([0-9]{1,2}(?:\.[0-9])?)(?:px)?\b/i)?.[1];
    if (micrSize) {
        const value = clamp(Number(micrSize), 16, 32);
        setField('micrFontSize', value, `Set MICR size to ${value}px`);
    } else if (/\bstandard micr\b/.test(p)) {
        setField('micrFontSize', 22, 'Set the standard MICR size to 22px');
    }
    if (/\bshow\s+(?:the\s+)?micr\s+(?:clear\s+)?band\b/.test(p)) {
        setControl('showMicrBand', true, current?.showMicrBand, 'Showed the white MICR clear band');
    } else if (/\bhide\s+(?:the\s+)?micr\s+(?:clear\s+)?band\b/.test(p)) {
        setControl('showMicrBand', false, current?.showMicrBand, 'Hid the white MICR clear band');
    }
    if (/\bshow\s+(?:the\s+)?amount\s+box\s+border\b/.test(p)) {
        setControl('showAmountBorder', true, current?.showAmountBorder, 'Showed the amount box border');
    } else if (/\bhide\s+(?:the\s+)?amount\s+box\s+border\b|\bremove\s+(?:the\s+)?amount\s+box\s+border\b/.test(p)) {
        setControl('showAmountBorder', false, current?.showAmountBorder, 'Hid the amount box border');
    }
    if (/\bshow\s+(?:the\s+)?micr\s+(?:clear\s+zone\s+)?guide\b/.test(p)) {
        setControl('showMicrGuide', true, current?.showMicrGuide, 'Showed the on-screen MICR clear-zone guide');
    } else if (/\bhide\s+(?:the\s+)?micr\s+(?:clear\s+zone\s+)?guide\b/.test(p)) {
        setControl('showMicrGuide', false, current?.showMicrGuide, 'Hid the on-screen MICR clear-zone guide');
    }

    // Back-style commands are user overrides: the feedback names the
    // per-design back they override (the deposit style marks the design
    // as touched in CheckBuilder, so the variant's own default no
    // longer applies until the front design changes).
    const backVariantName = resolveBackVariant(current?.checkStyle || 'personal', null).variantName;
    const backMatches: Array<{ pattern: RegExp; id: string; label: string }> = [
        { pattern: /\bmobile\s+deposit\s+back\b|\bback\s+(?:to\s+|style\s+)?mobile(?:\s+deposit)?\b/, id: 'mobile', label: 'Mobile Deposit back' },
        { pattern: /\batm(?:\s*\/\s*branch)?\s+back\b|\bback\s+(?:to\s+|style\s+)?atm\b/, id: 'atm', label: 'ATM / Branch Deposit back' },
        { pattern: /\bbusiness\s+back\b|\bback\s+(?:to\s+|style\s+)?business\b/, id: 'business', label: 'Corporate / Business back' },
        { pattern: /\bstandard\s+back\b|\bback\s+(?:to\s+|style\s+)?standard\b/, id: 'standard', label: 'Standard back' }
    ];
    for (const back of backMatches) {
        if (back.pattern.test(p)) {
            setControl('backSideStyle', back.id, current?.backSideStyle, `Set the back to ${back.label} (overrides the ${backVariantName} back)`);
            break;
        }
    }
    if (result.controls.backSideStyle === undefined && depositStyleIntent) {
        const depositLabels: Record<string, string> = {
            mobile: 'Mobile Deposit', atm: 'ATM / Branch Deposit',
            business: 'Corporate / Business', standard: 'Standard'
        };
        setControl('backSideStyle', depositStyleIntent, current?.backSideStyle,
            `Set the deposit style to ${depositLabels[depositStyleIntent]} (back of the check, overrides the ${backVariantName} back)`);
    }
    if (result.controls.backSideStyle === undefined && /\bback\b/.test(p)) {
        if (/\bmobile\s+deposit\b/.test(p)) setControl('backSideStyle', 'mobile', current?.backSideStyle, `Set the back to Mobile Deposit back (overrides the ${backVariantName} back)`);
        else if (/\batm\b/.test(p)) setControl('backSideStyle', 'atm', current?.backSideStyle, `Set the back to ATM / Branch Deposit back (overrides the ${backVariantName} back)`);
        else if (/\bbusiness\b|\bcorporate\b/.test(p)) setControl('backSideStyle', 'business', current?.backSideStyle, `Set the back to Corporate / Business back (overrides the ${backVariantName} back)`);
        else if (/\bstandard\b|\bpersonal\b/.test(p)) setControl('backSideStyle', 'standard', current?.backSideStyle, `Set the back to Standard back (overrides the ${backVariantName} back)`);
    }

    const handwritingMatches: Array<{ pattern: RegExp; id: string; label: string }> = [
        { pattern: /\btyped\s+handwriting\b|\bno\s+handwriting\b/, id: 'none', label: 'typed handwriting' },
        { pattern: /\bballpoint\s+handwriting\b/, id: 'ballpoint', label: 'Ballpoint handwriting' },
        { pattern: /\bfountain\s+(?:pen\s+)?handwriting\b/, id: 'fountain', label: 'Fountain Pen handwriting' },
        { pattern: /\bfelt\s+tip\s+handwriting\b/, id: 'felt_tip', label: 'Felt Tip handwriting' },
        { pattern: /\bcursive\s+handwriting\b/, id: 'cursive', label: 'Cursive handwriting' }
    ];
    for (const hand of handwritingMatches) {
        if (hand.pattern.test(p)) {
            setControl('handwritingStyle', hand.id, current?.handwritingStyle, `Set handwriting to ${hand.label}`);
            break;
        }
    }
    const signatureMatches: Array<{ pattern: RegExp; id: string; label: string }> = [
        { pattern: /\btyped\s+signature\b|\bno\s+signature\s+style\b/, id: 'none', label: 'typed signature' },
        { pattern: /\bballpoint\s+signature\b/, id: 'ballpoint', label: 'Ballpoint signature' },
        { pattern: /\bfountain\s+(?:pen\s+)?signature\b/, id: 'fountain', label: 'Fountain Pen signature' },
        { pattern: /\bfelt\s+tip\s+signature\b/, id: 'felt_tip', label: 'Felt Tip signature' },
        { pattern: /\bcursive\s+signature\b/, id: 'cursive', label: 'Cursive signature' }
    ];
    for (const signature of signatureMatches) {
        if (signature.pattern.test(p)) {
            setControl('signatureStyle', signature.id, current?.signatureStyle, `Set the signature style to ${signature.label}`);
            break;
        }
    }
    if (/\bstraighten\s+(?:the\s+)?(?:signature|handwriting)\b/.test(p)) {
        setControl('handwritingRotation', 0, current?.handwritingRotation, 'Straightened the handwriting angle');
    } else {
        const angle = raw.match(/\b(?:signature|handwriting)\s+angle\s*(?:to|is|:|=)?\s*(-?[0-9]+(?:\.[0-9])?)\s*(?:degrees?|°)?\b/i)?.[1];
        if (angle) {
            const value = clamp(Number(angle), -20, 20);
            setControl('handwritingRotation', value, current?.handwritingRotation, `Set the handwriting angle to ${value}°`);
        }
    }
    if (/\b(?:remove|delete|clear)\s+(?:the\s+)?logo\b/.test(p)) {
        setControl('clearLogo', true, false, 'Removed the uploaded logo');
    }
    if (/\b(?:remove|delete|clear)\s+(?:the\s+)?(?:uploaded\s+)?signature\s+image\b/.test(p)) {
        setControl('clearSignature', true, false, 'Removed the uploaded signature image');
    }

    // ---- Layout ---------------------------------------------------------------------
    if (/\breset\s+(?:the\s+)?layout\b|\brestore\s+(?:the\s+)?(?:default|standard)\s+layout\b|\bstandard\s+layout\b/.test(p)) {
        result.layout.resetToDefault = true;
        addChange('layout:reset', 'Reset all dragged elements to the standard layout');
    }
    if (/\brestore\s+(?:the\s+)?deleted\b|\bbring\s+back\s+(?:the\s+)?deleted\b|\bundo\s+deletions\b/.test(p)) {
        result.layout.restoreDeleted = true;
        addChange('layout:restoreDeleted', 'Restored deleted front and back elements');
    }

    if (!result.layout.resetToDefault) {
        for (const object of OBJECT_DEFINITIONS) {
            const aliases = [...object.aliases].sort((a, b) => b.length - a.length);
            const aliasPattern = aliases.map(escapeRegExp).join('|');
            const currentPosition = current?.objectPositions?.[object.id];
            const base = { ...defaultPosition(object.id, width, height), ...(currentPosition || {}) };
            let move: AiObjectMove | undefined;

            const scalePercent = p.match(new RegExp(`\\bscale\\s+(?:the\\s+)?(?:${aliasPattern})\\s+(?:to\\s+)?([0-9]{2,3})\\s*%`));
            const scaleWord = p.match(new RegExp(`\\b(?:make|set)\\s+(?:the\\s+)?(?:${aliasPattern})\\s+(bigger|larger|smaller)\\b`));
            if (scalePercent || scaleWord) {
                const factor = scalePercent ? Number(scalePercent[1]) / 100 : (scaleWord?.[1] === 'smaller' ? 0.8 : 1.25);
                const baseScaleX = base.scaleX ?? 1;
                const baseScaleY = base.scaleY ?? 1;
                move = {
                    scaleX: clamp(baseScaleX * factor, 0.1, 4),
                    scaleY: clamp(baseScaleY * factor, 0.1, 4)
                };
                addChange(`layout:scale:${object.id}`, `Scaled the ${object.label} to ${Math.round(factor * 100)}% of its current size`);
            }

            const namedLocation = p.match(new RegExp(`\\b(?:move|shift|place|put|position)\\s+(?:the\\s+)?(?:${aliasPattern})\\s+(?:to\\s+)?(?:the\\s+)?(top left|top right|bottom left|bottom right|center|centre)\\b`));
            const direction = p.match(new RegExp(`\\b(?:move|shift|nudge)\\s+(?:the\\s+)?(?:${aliasPattern})\\s+(left|right|up|down)(?:\\s+by)?\\s*([0-9]{1,3})?\\s*(?:px|pixels)?\\b`));
            if (namedLocation) {
                const location = namedLocation[1];
                const left = base.left ?? 40;
                const top = base.top ?? 40;
                if (location === 'top left') move = { ...(move || {}), left: 40, top: 24 };
                else if (location === 'top right') move = { ...(move || {}), left: Math.max(20, width - object.width - 40), top: 24 };
                else if (location === 'bottom left') move = { ...(move || {}), left: 40, top: Math.max(24, height - 105) };
                else if (location === 'bottom right') move = { ...(move || {}), left: Math.max(20, width - object.width - 40), top: Math.max(24, height - 105) };
                else move = { ...(move || {}), left: width / 2, top: height / 2 };
                if (object.id === 'watermark' && location !== 'center' && location !== 'centre') {
                    move.left = location.includes('right') ? width - 180 : 180;
                }
                if (left === move.left && top === move.top && !scalePercent && !scaleWord) move = undefined;
                else addChange(`layout:move:${object.id}`, `Moved the ${object.label} to the ${location}`);
            } else if (direction) {
                const amount = Number(direction[2] || 20);
                const left = base.left ?? 40;
                const top = base.top ?? 40;
                move = { ...(move || {}) };
                if (direction[1] === 'left') move.left = clamp(left - amount, 0, width - 20);
                if (direction[1] === 'right') move.left = clamp(left + amount, 0, width - 20);
                if (direction[1] === 'up') move.top = clamp(top - amount, 0, height - 70);
                if (direction[1] === 'down') move.top = clamp(top + amount, 0, height - 70);
                addChange(`layout:move:${object.id}`, `Moved the ${object.label} ${direction[1]} by ${amount}px`);
            }

            if (move) {
                // The MICR line itself is not an AI-movable object. Its render
                // position is guarded by the compliance code, so never return
                // a move for it or for any object into the clear band.
                if (move.top !== undefined) move.top = clamp(move.top, 0, height - 70);
                if (move.left !== undefined) move.left = clamp(move.left, 0, width - 20);
                result.layout.moveMap[object.id] = move;
            }
        }
    }
    if (/\bmove\s+(?:the\s+)?micr\b/.test(p)) {
        addWarning('The MICR line was not moved: its position is locked to the bank clear-band standard.');
    }
    if (/\b(?:add|insert|put)\s+(?:a\s+)?(?:qr\s*code|barcode)\b/.test(p)) {
        addWarning('The check designer has no QR-code or barcode element, so that element was not added.');
    }
    if (/\badd\s+(?:my\s+|the\s+)?logo\b/.test(p) && !/\bupload(?:ed)?\b/.test(p)) {
        addWarning('Use the logo upload control to add a logo file; the prompt alone cannot supply the image.');
    }
    if (/\bguarantee(?:d)?\s+(?:bank\s+)?acceptance\b|\bbank[ -]?proof\b/.test(p)) {
        addWarning('No design prompt can guarantee bank acceptance; physical MICR toner and check stock are also required.');
    }

    if (changes.length === 0 && warnings.length === 0) {
        addWarning('I could not match that to a change I can make, so nothing was changed. I can: apply a bank template or check layout ("Chase business check"), set the border style, colour and thickness, paper colour, texture and fonts, turn security features on or off, set the back and deposit style, the medallion colour and finish, the ink colour, move or resize elements, and fill in check fields (payee, amount, memo, routing, account, date).');
    }

    result.meta.changes = changes;
    result.meta.warnings = warnings;
    result.meta.confidence = changes.length === 0
        ? 0.2
        : clamp(0.68 + changes.length * 0.05 - warnings.length * 0.04, 0.35, 0.98);
    result.meta.explanation = [
        changes.length ? `Applied: ${changes.join('; ')}.` : 'No supported changes were applied.',
        warnings.length ? `Not applied: ${warnings.join(' ')}` : ''
    ].filter(Boolean).join(' ');

    return result;
};
